import {
  ApprovalTerms,
  ApprovalBadge,
  ComparisonRowConfig,
  LenderApproval,
  GqlCreditSubmissionApproval,
  GqlLenderFee,
  Condition,
} from '../types';

type TermKey = keyof Omit<ApprovalTerms, 'conditions' | 'lenderFees'>;

/**
 * Determines which lenders get "best" and "worst" badges for a given row.
 * The `favorable` flag indicates whether the value is the best (true) or worst (false) for that metric.
 */
export function computeBadges(approvals: LenderApproval[], config: ComparisonRowConfig): Record<string, ApprovalBadge> {
  const result: Record<string, ApprovalBadge> = {};

  if (!config.bestIs || approvals.length < 2) return result;

  const key = config.key as TermKey;

  const numericValues = approvals
    .map(a => {
      const raw = a.approvalTerms[key];
      const value = typeof raw === 'number' ? raw : null;
      return { id: a.id, value };
    })
    .filter((entry): entry is { id: string; value: number } => entry.value !== null);

  if (numericValues.length < 2) return result;

  const sorted = [...numericValues].sort((a, b) => a.value - b.value);
  const minEntry = sorted[0];
  const maxEntry = sorted[sorted.length - 1];

  if (minEntry.value === maxEntry.value) return result;

  if (config.bestIs === 'highest') {
    result[maxEntry.id] = { label: 'highest', favorable: true };
    result[minEntry.id] = { label: 'lowest', favorable: false };
  } else {
    result[minEntry.id] = { label: 'lowest', favorable: true };
    result[maxEntry.id] = { label: 'highest', favorable: false };
  }

  return result;
}

/**
 * Computes badges specifically for the conditions count row.
 * Fewer conditions = favorable.
 */
export function computeConditionsBadges(approvals: LenderApproval[]): Record<string, ApprovalBadge> {
  const result: Record<string, ApprovalBadge> = {};

  if (approvals.length < 2) return result;

  const entries = approvals.map(a => ({
    id: a.id,
    count: a.approvalTerms.conditions.length,
  }));

  const sorted = [...entries].sort((a, b) => a.count - b.count);
  const minEntry = sorted[0];
  const maxEntry = sorted[sorted.length - 1];

  if (minEntry.count === maxEntry.count) return result;

  result[minEntry.id] = { label: 'fewest', favorable: true };
  result[maxEntry.id] = { label: 'most', favorable: false };

  return result;
}

// ---------------------------------------------------------------------------
// API → UI mapper
// ---------------------------------------------------------------------------

const APPROVED_LABELS = new Set(['Approved', 'Pre-Approved']);
// Raw Dynamics creditSubStage numeric codes that represent approvals
const APPROVED_STAGE_CODES = new Set(['804790003', '804790013']);

/**
 * Returns true when a credit submission represents an approval.
 * Checks both the human-readable decision field and the raw Dynamics stage code.
 */
export function isApproved(cs: { stage: string | null; decision: string | null }): boolean {
  return APPROVED_LABELS.has(cs.decision ?? '') || APPROVED_STAGE_CODES.has(cs.stage ?? '');
}

/**
 * Splits a contingencies string (plain text) into structured Condition objects.
 * Handles numbered lists, bullet points, semicolons, and plain newlines.
 */
export function parseContingencies(contingencies: string | null): Condition[] {
  if (!contingencies?.trim()) return [];
  return contingencies
    .split(/\n|;|(?=\d+\.\s)/)
    .map(s => s.replace(/^\d+\.\s*/, '').trim())
    .filter(Boolean)
    .map((description, index) => ({ id: `cond-${index}`, description }));
}

/**
 * Maps a raw GqlCreditSubmissionApproval from the API to the internal LenderApproval shape.
 * Picks the selected proposal (status === 'selected') for term / monthly-payment, or falls
 * back to the first proposal in the list.
 */
export function mapCreditSubmissionToApproval(cs: GqlCreditSubmissionApproval): LenderApproval {
  // At this point cs.creditDecisionParsedData is guaranteed to exist (filtered upstream).
  const parsed = cs.creditDecisionParsedData!.parsedData;

  const conditions: Condition[] =
    parsed.conditions && parsed.conditions.length > 0
      ? parsed.conditions
          .filter((d): d is string => d != null)
          .map((description, index) => ({ id: `cond-${index}`, description }))
      : [];

  // Select proposal for term / monthly payment: prefer status === 'selected', fall back to first.
  const proposals = (parsed as any)?.proposals as any[] | undefined;
  const selectedProposal =
    Array.isArray(proposals) && proposals.length > 0
      ? proposals.find(p => p?.status === 'selected') ?? proposals[0]
      : undefined;

  const selectedTermLengthMonths =
    (selectedProposal && selectedProposal.termLengthMonths != null
      ? selectedProposal.termLengthMonths
      : parsed.termLengthMonths) ?? null;

  const selectedMonthlyPayment =
    selectedProposal && selectedProposal.monthlyPayment != null
      ? selectedProposal.monthlyPayment
      : null;

  return {
    id: cs.creditSubmissionId,
    lenderName: cs.lenderName ?? 'Unknown Lender',
    parsedDecisionId: cs.creditDecisionParsedData!.parsedDecisionId ?? null,
    approvalTerms: {
      approvedAmount: parsed.approvalAmount ?? null,
      interestRate: parsed.interestRate ?? null,
      termLengthMonths: selectedTermLengthMonths,
      monthlyPayment: selectedMonthlyPayment,
      conditions,
      lenderFees: (parsed.lenderFees ?? []).filter((f): f is GqlLenderFee => f != null),
    },
  };
}

/**
 * Formats a cell value based on its configured format type.
 */
export function formatCellValue(
  value: string | number | null | undefined,
  format: ComparisonRowConfig['format']
): string {
  if (value === null || value === undefined) return '—';

  switch (format) {
    case 'currency':
      return `$${Number(value).toLocaleString('en-US')}`;
    case 'percentage':
      return `${value}%`;
    case 'months':
      return `${value} mo`;
    case 'number':
      return String(value);
    case 'text':
    default:
      return String(value);
  }
}
